import {
  appendCardsToColumn,
  applySyncedTaskCardToColumns,
  findColumnIdByCardId,
  mergeSyncedTaskCardWithoutColumnMove,
  removeCardFromColumns,
  replaceColumnCards,
} from './taskTrackerKanbanDnd.utils'
import { mergeTaskStickerValues } from './taskTrackerTaskStickers.utils'
import type {
  TaskTrackerBoardCard,
  TaskTrackerBoardColumn,
  TaskTrackerBoardLaneColumn,
  TaskTrackerBoardLaneItem,
  TaskTrackerBoardLaneSummary,
} from '../store/taskTracker.types'

export const isTaskTrackerBoardLaneColumn = (
  item: TaskTrackerBoardLaneItem,
): item is TaskTrackerBoardLaneColumn => item.type === 'column'

export const isTaskTrackerBoardLaneSummary = (
  item: TaskTrackerBoardLaneItem,
): item is TaskTrackerBoardLaneSummary => item.type === 'summary'

export const extractColumnsFromBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
): TaskTrackerBoardColumn[] =>
  boardItems.filter(isTaskTrackerBoardLaneColumn)

export const mergeColumnsIntoBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
  columns: TaskTrackerBoardColumn[],
): TaskTrackerBoardLaneItem[] => {
  const columnsById = new Map(columns.map((column) => [column.id, column]))

  return boardItems.map((item) => {
    if (!isTaskTrackerBoardLaneColumn(item)) return item

    const updated = columnsById.get(item.id)
    if (!updated) return item

    return {
      ...item,
      ...updated,
      type: 'column' as const,
    }
  })
}

export const sortBoardItemsByPosition = (
  boardItems: TaskTrackerBoardLaneItem[],
): TaskTrackerBoardLaneItem[] =>
  [...boardItems].sort((left, right) => {
    if (left.position !== right.position) {
      return left.position - right.position
    }

    if (left.type !== right.type) {
      return left.type.localeCompare(right.type)
    }

    return left.id - right.id
  })

export const getBoardLaneItemKey = (item: TaskTrackerBoardLaneItem) =>
  `${item.type}:${item.id}`

export const appendCardsToBoardItemColumn = (
  boardItems: TaskTrackerBoardLaneItem[],
  columnId: number,
  newCards: TaskTrackerBoardCard[],
  totalCount: number,
): TaskTrackerBoardLaneItem[] =>
  mergeColumnsIntoBoardItems(
    boardItems,
    appendCardsToColumn(
      extractColumnsFromBoardItems(boardItems),
      columnId,
      newCards,
      totalCount,
    ),
  )

export const replaceBoardItemColumnCards = (
  boardItems: TaskTrackerBoardLaneItem[],
  columnId: number,
  cards: TaskTrackerBoardCard[],
  totalCount: number,
  page?: {
    hasMore?: boolean
    cardsLimit?: number
  },
): TaskTrackerBoardLaneItem[] => {
  const next = mergeColumnsIntoBoardItems(
    boardItems,
    replaceColumnCards(
      extractColumnsFromBoardItems(boardItems),
      columnId,
      cards,
      totalCount,
    ),
  )

  if (!page) return next

  return next.map((item) => {
    if (item.type !== 'column' || item.id !== columnId) return item

    return {
      ...item,
      cardsTotalCount: totalCount,
      cardsHasMore: page.hasMore ?? item.cardsHasMore,
      cardsLimit: page.cardsLimit ?? item.cardsLimit,
    }
  })
}

export const appendCardsToBoardItemSummary = (
  boardItems: TaskTrackerBoardLaneItem[],
  summaryId: number,
  newCards: TaskTrackerBoardCard[],
  page: {
    totalCount: number
    hasMore: boolean
    nextCursor: string | null
  },
): TaskTrackerBoardLaneItem[] =>
  boardItems.map((item) => {
    if (item.type !== 'summary' || item.id !== summaryId) return item

    const existingIds = new Set(item.cards.map((card) => card.id))
    const cardsToAppend = newCards.filter((card) => !existingIds.has(card.id))
    const cards = [...item.cards, ...cardsToAppend]

    return {
      ...item,
      cards,
      cardsCount: cards.length,
      cardsTotalCount: page.totalCount,
      cardsHasMore: page.hasMore,
      nextCursor: page.nextCursor,
    }
  })

export const findSummaryIdsContainingCard = (
  boardItems: TaskTrackerBoardLaneItem[],
  taskId: number,
): number[] =>
  boardItems
    .filter(
      (item): item is TaskTrackerBoardLaneSummary =>
        item.type === 'summary' && item.cards.some((card) => card.id === taskId),
    )
    .map((item) => item.id)

export const replaceCardInBoardItemSummaries = (
  boardItems: TaskTrackerBoardLaneItem[],
  updatedCard: TaskTrackerBoardCard,
): TaskTrackerBoardLaneItem[] =>
  boardItems.map((item) => {
    if (item.type !== 'summary') return item

    const cardIndex = item.cards.findIndex((card) => card.id === updatedCard.id)
    if (cardIndex === -1) return item

    const nextCards = [...item.cards]
    nextCards[cardIndex] = {
      ...nextCards[cardIndex],
      ...updatedCard,
      stickers: mergeTaskStickerValues(
        nextCards[cardIndex].stickers,
        updatedCard.stickers,
      ),
      columnId: nextCards[cardIndex].columnId,
    }

    return {
      ...item,
      cards: nextCards,
    }
  })

export const syncUpdatedCardInBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
  updatedCard: TaskTrackerBoardCard,
  options?: { preserveColumnPosition?: boolean },
): TaskTrackerBoardLaneItem[] => {
  const columns = extractColumnsFromBoardItems(boardItems)
  const cardInColumns = findColumnIdByCardId(columns, updatedCard.id) != null

  let next = boardItems
  if (cardInColumns) {
    const nextColumns = options?.preserveColumnPosition
      ? mergeSyncedTaskCardWithoutColumnMove(columns, updatedCard)
      : applySyncedTaskCardToColumns(columns, updatedCard)
    next = mergeColumnsIntoBoardItems(boardItems, nextColumns)
  }

  return replaceCardInBoardItemSummaries(next, updatedCard)
}

export const replaceBoardItemSummaryCards = (
  boardItems: TaskTrackerBoardLaneItem[],
  summaryId: number,
  cards: TaskTrackerBoardCard[],
  page: {
    totalCount: number
    hasMore: boolean
    nextCursor: string | null
  },
): TaskTrackerBoardLaneItem[] =>
  boardItems.map((item) => {
    if (item.type !== 'summary' || item.id !== summaryId) return item

    return {
      ...item,
      cards,
      cardsCount: cards.length,
      cardsTotalCount: page.totalCount,
      cardsHasMore: page.hasMore,
      nextCursor: page.nextCursor,
    }
  })

export const applyBoardRefreshItemsToBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
  result: {
    columns: Array<{
      columnId: number
      items: TaskTrackerBoardCard[]
      hasMore: boolean
      totalCount: number
      limit: number
    }>
    summaries: Array<{
      summaryId: number
      items: TaskTrackerBoardCard[]
      hasMore: boolean
      totalCount: number
      nextCursor: string | null
    }>
  },
): TaskTrackerBoardLaneItem[] => {
  let next = boardItems

  result.columns.forEach((column) => {
    next = replaceBoardItemColumnCards(
      next,
      column.columnId,
      column.items,
      column.totalCount,
      {
        hasMore: column.hasMore,
        cardsLimit: column.limit,
      },
    )
  })

  result.summaries.forEach((summary) => {
    next = replaceBoardItemSummaryCards(
      next,
      summary.summaryId,
      summary.items,
      {
        totalCount: summary.totalCount,
        hasMore: summary.hasMore,
        nextCursor: summary.nextCursor,
      },
    )
  })

  return next
}

export const removeCardFromBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
  taskId: number,
): TaskTrackerBoardLaneItem[] => {
  const nextColumns = removeCardFromColumns(
    extractColumnsFromBoardItems(boardItems),
    taskId,
  )

  return mergeColumnsIntoBoardItems(
    boardItems.map((item) => {
      if (item.type !== 'summary') return item

      const cards = item.cards.filter((card) => card.id !== taskId)
      if (cards.length === item.cards.length) return item

      return {
        ...item,
        cards,
        cardsCount: cards.length,
        cardsTotalCount: Math.max(cards.length, item.cardsTotalCount - 1),
      }
    }),
    nextColumns,
  )
}
